import { useEffect, useRef } from "preact/hooks";
import type { RefObject } from "preact";

interface UseIntersectionObserverOptions {
  /** Fired when the sentinel enters the root/viewport. */
  onIntersect: () => void;
  /** Only observe while true (e.g. hasNextPage && sheet open && !fetching). */
  enabled?: boolean;
  /**
   * Ref to the scroll container used as the observer root. Read inside the
   * effect (not during render) so the element is populated by the time we
   * observe. Falls back to the viewport when absent.
   */
  rootRef?: RefObject<Element | null>;
  rootMargin?: string;
  threshold?: number | number[];
}

/**
 * Observes a returned `targetRef` and invokes `onIntersect` when it scrolls into
 * view. Used to drive infinite scroll: place the ref on a sentinel at the bottom
 * of a scroll container. `rootMargin` defaults to "200px" to pre-fetch slightly
 * before the sentinel is fully visible for a smoother feel.
 */
export function useIntersectionObserver({
  onIntersect,
  enabled = true,
  rootRef,
  rootMargin = "200px",
  threshold = 0,
}: UseIntersectionObserverOptions) {
  const targetRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without re-subscribing the observer each render —
  // `onIntersect` closes over `fetchNextPage` and changes identity often.
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  useEffect(() => {
    if (!enabled) return;
    const node = targetRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onIntersectRef.current();
        }
      },
      { root: rootRef?.current ?? null, rootMargin, threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootRef, rootMargin, threshold]);

  return targetRef;
}
